/**
 * Khung một tiểu đội ở thẻ "Chất lượng tháng" khi máy tự đếm (CEO 01/11/2026): số máy đếm + tỷ lệ +
 * Đạt / Chưa đạt, sửa tay có lý do, về số máy, khách cần hỏi và soi mẫu. Danh sách khách và soi mẫu
 * chỉ đến tay người được giao kiểm: máy chủ đã bỏ tiểu đội người gọi đang thuộc.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import { QueryErrorNote } from "@/components/QueryErrorNote";
import { formatDate, formatDateTime } from "@/lib/format";
import {
  type SalesQualityMark,
  type SalesQualityMilestone,
  type SalesQualitySurveyItem,
  useAnswerSurvey,
  useRevertQuality,
  useRuleMilestone,
  useSaveQuality,
} from "../api";
import { CARE_KIND_LABEL } from "../labels";
import { SquadMark } from "./marks";

const CARE_FIELDS = [
  { key: "careDue", label: "Lượt chăm phải làm" },
  { key: "careOnTime", label: "Lượt chăm đúng hẹn" },
] as const;

const SURVEY_FIELDS = [
  { key: "delivered", label: "Khách đã giao ảnh" },
  { key: "surveyed", label: "Khách đã hỏi" },
  { key: "satisfied", label: "Khách hài lòng" },
] as const;

type FieldKey = (typeof CARE_FIELDS)[number]["key"] | (typeof SURVEY_FIELDS)[number]["key"];

const percent = (num: number, den: number) => `${Math.round((num * 100) / den)}%`;

function carePass(m: SalesQualityMark): string {
  if (m.careDue == null || m.careOnTime == null) return "Chưa có số";
  if (m.careDue === 0) return "Không có lượt nào · Đạt";
  const rate = percent(m.careOnTime, m.careDue);
  if (!m.onTimeMinBp) return rate;
  return `${rate} · ${m.careOnTime * 10_000 >= m.onTimeMinBp * m.careDue ? "Đạt" : "Chưa đạt"}`;
}

function surveyPass(m: SalesQualityMark): string {
  if (m.surveyed == null || m.satisfied == null) return "Chưa có số";
  if (m.surveyed === 0) return "Chưa hỏi khách nào";
  const rate = percent(m.satisfied, m.surveyed);
  if (!m.satisfiedMinBp) return rate;
  return `${rate} · ${m.satisfied * 10_000 >= m.satisfiedMinBp * m.surveyed ? "Đạt" : "Chưa đạt"}`;
}

function parseCount(raw: string): number | null {
  const t = raw.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isInteger(n) && n >= 0 ? n : null;
}

const message = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);

function SourceNote({ who, at, reason, onRevert, label, busy, canEdit }: {
  who: string;
  at: string;
  reason: string;
  onRevert: () => void;
  label: string;
  busy: boolean;
  canEdit: boolean;
}) {
  return (
    <div className="flex flex-col gap-1 text-xs text-muted md:flex-row md:items-center md:justify-between">
      <span className="min-w-0">
        Sửa tay bởi {who} lúc {formatDateTime(at)}. Lý do: {reason}
      </span>
      {canEdit ? (
        <Button variant="outline" size="sm" aria-label={label} disabled={busy} onClick={onRevert}>
          Về số máy
        </Button>
      ) : null}
    </div>
  );
}

function EditForm({ mark, name, month, onDone }: { mark: SalesQualityMark; name: string; month: string; onDone: () => void }) {
  const save = useSaveQuality(month);
  const [row, setRow] = useState<Record<FieldKey, number | null>>({
    careDue: mark.careDue ?? null,
    careOnTime: mark.careOnTime ?? null,
    delivered: mark.delivered ?? null,
    surveyed: mark.surveyed ?? null,
    satisfied: mark.satisfied ?? null,
  });
  const [careReason, setCareReason] = useState(mark.careReason ?? "");
  const [surveyReason, setSurveyReason] = useState(mark.surveyReason ?? "");
  const careChanged = row.careDue !== (mark.careDue ?? null) || row.careOnTime !== (mark.careOnTime ?? null);
  const surveyChanged = row.delivered !== (mark.delivered ?? null) || row.surveyed !== (mark.surveyed ?? null) || row.satisfied !== (mark.satisfied ?? null);

  const submit = () => {
    if ((careChanged && !careReason.trim()) || (surveyChanged && !surveyReason.trim())) {
      toast.error("Sửa số của máy đếm phải ghi lý do");
      return;
    }
    // Nhóm theo máy mà người dùng không đụng thì gửi trống (= theo máy): số máy có thể đã đổi từ
    // lúc mở form, gửi lại số cũ sẽ bị máy chủ coi là sửa tay và đòi lý do. Nhóm đang sửa tay thì
    // gửi nguyên số + lý do cũ để giữ bản sửa tay.
    const keepCare = careChanged || mark.careSource === "manual";
    const keepSurvey = surveyChanged || mark.surveySource === "manual";
    if (!keepCare && !keepSurvey) {
      onDone(); // không đổi số nào: vẫn theo máy, không gửi gì
      return;
    }
    save.mutate([{
      squadId: mark.squadId,
      careDue: keepCare ? row.careDue : null,
      careOnTime: keepCare ? row.careOnTime : null,
      careReason: keepCare ? careReason : "",
      delivered: keepSurvey ? row.delivered : null,
      surveyed: keepSurvey ? row.surveyed : null,
      satisfied: keepSurvey ? row.satisfied : null,
      surveyReason: keepSurvey ? surveyReason : "",
      note: mark.note,
    }], {
      onSuccess: () => {
        toast.success("Đã lưu số sửa");
        onDone();
      },
      onError: (e) => toast.error(message(e, "Không lưu được số sửa")),
    });
  };

  const field = (f: { key: FieldKey; label: string }) => (
    <label key={f.key} className="flex min-w-0 flex-col gap-1 text-sm">
      <span className="text-muted">{f.label}</span>
      <Input
        inputMode="numeric"
        aria-label={`${f.label} — ${name}`}
        value={row[f.key] === null ? "" : String(row[f.key])}
        onChange={(e) => setRow({ ...row, [f.key]: parseCount(e.target.value) })}
      />
    </label>
  );

  return (
    <div className="space-y-2 rounded-md border border-border p-3">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5">
        {CARE_FIELDS.map(field)}
        {SURVEY_FIELDS.map(field)}
      </div>
      <Input aria-label={`Lý do sửa số chăm khách — ${name}`} placeholder="Lý do sửa số chăm khách" value={careReason} onChange={(e) => setCareReason(e.target.value)} />
      <Input aria-label={`Lý do sửa số hỏi khách — ${name}`} placeholder="Lý do sửa số hỏi khách" value={surveyReason} onChange={(e) => setSurveyReason(e.target.value)} />
      <div className="flex flex-col gap-2 md:flex-row">
        <Button aria-label={`Lưu số sửa — ${name}`} disabled={save.isPending} onClick={submit}>
          {save.isPending ? "Đang lưu…" : "Lưu số sửa"}
        </Button>
        <Button variant="outline" onClick={onDone}>
          Huỷ
        </Button>
      </div>
    </div>
  );
}

const ANSWERS = [
  { value: "satisfied", label: "Hài lòng" },
  { value: "unsatisfied", label: "Không hài lòng" },
  { value: "unreachable", label: "Không liên lạc được" },
] as const;

function SurveyList({ items, month }: { items: SalesQualitySurveyItem[]; month: string }) {
  const answer = useAnswerSurvey(month);
  if (items.length === 0) return null;
  return (
    <section className="space-y-2">
      <h4 className="text-sm font-semibold">Khách cần hỏi</h4>
      <ul className="space-y-2">
        {items.map((it) => (
          <li key={it.id} data-testid={`survey-${it.id}`} className="flex flex-col gap-2 rounded-md border border-border p-2 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0 text-sm">
              <p className="font-medium">{it.customer}</p>
              <p className="text-muted">
                <a className="underline" href={`tel:${it.phone}`}>{it.phone}</a> · {it.orderCode}
              </p>
            </div>
            <div className="flex flex-col gap-2 md:flex-row">
              {ANSWERS.map((a) => (
                <Button
                  key={a.value}
                  variant={it.answer === a.value ? "default" : "outline"}
                  disabled={answer.isPending}
                  onClick={() =>
                    answer.mutate({ id: it.id, answer: a.value }, { onError: (e) => toast.error(message(e, "Không ghi được câu trả lời")) })
                  }
                >
                  {a.label}
                </Button>
              ))}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function MilestoneRow({ m, month }: { m: SalesQualityMilestone; month: string }) {
  const rule = useRuleMilestone(month);
  const [ruling, setRuling] = useState<"rejected" | "excused" | null>(null);
  const [note, setNote] = useState("");
  // "Bây giờ" chụp một lần lúc dựng dòng (initializer của useState): render phải thuần.
  const [now] = useState(() => Date.now());
  // Excused milestones leave the count, so they are never late; an open one is late only past its deadline.
  const late = m.ruling !== "excused" && (m.doneAt ? new Date(m.doneAt).getTime() : now) > new Date(m.dueAt).getTime();
  const send = (value: "" | "rejected" | "excused", why: string) =>
    rule.mutate(
      { month, sourceSystem: m.sourceSystem, orderId: m.orderId, kind: m.kind, ruling: value, note: why },
      {
        onSuccess: () => {
          setRuling(null);
          setNote("");
        },
        onError: (e) => toast.error(message(e, "Không ghi được phán quyết")),
      },
    );
  return (
    <li data-testid={`milestone-${m.orderId}-${m.kind}`} className="space-y-2 rounded-md border border-border p-2">
      <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0 text-sm">
          <p className="font-medium">{m.orderCode} · {CARE_KIND_LABEL[m.kind] ?? m.kind}</p>
          <p className="text-muted">
            Hạn {formatDate(m.dueAt)} · {m.doneAt ? `${m.auto ? "Máy ghi" : m.doneByName || m.doneBy} lúc ${formatDateTime(m.doneAt)}` : "Chưa làm"}
            {late ? " · Trễ" : ""}
          </p>
          {m.ruling ? <p className="text-muted">{m.ruling === "rejected" ? "Đã bác" : "Đã miễn"}: {m.rulingNote}</p> : null}
        </div>
        <div className="flex flex-col gap-2 md:flex-row">
          {m.ruling ? (
            <Button variant="outline" disabled={rule.isPending} onClick={() => send("", "")}>Bỏ phán quyết</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => setRuling("rejected")}>Bác</Button>
              <Button variant="outline" onClick={() => setRuling("excused")}>Miễn</Button>
            </>
          )}
        </div>
      </div>
      {ruling ? (
        <div className="flex flex-col gap-2 md:flex-row">
          <Input aria-label="Lý do" placeholder="Lý do" value={note} onChange={(e) => setNote(e.target.value)} />
          <Button
            disabled={rule.isPending}
            onClick={() => {
              if (!note.trim()) {
                toast.error("Bác hay Miễn đều phải ghi lý do");
                return;
              }
              send(ruling, note);
            }}
          >
            Lưu phán quyết
          </Button>
        </div>
      ) : null}
    </li>
  );
}

type Props = {
  mark: SalesQualityMark;
  name: string;
  teamName: string;
  month: string;
  canRecord: boolean;
  survey: SalesQualitySurveyItem[];
  surveyError: unknown;
  milestones: SalesQualityMilestone[];
  milestonesError: unknown;
};

export function QualitySquadCard({ mark, name, teamName, month, canRecord, survey, surveyError, milestones, milestonesError }: Props) {
  const revert = useRevertQuality(month);
  const [editing, setEditing] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const cells: { label: string; value: number | null | undefined }[] = [
    { label: "Lượt chăm phải làm", value: mark.careDue },
    { label: "Lượt chăm đúng hẹn", value: mark.careOnTime },
    { label: "Khách đã giao ảnh", value: mark.delivered },
    { label: "Khách đã hỏi", value: mark.surveyed },
    { label: "Khách hài lòng", value: mark.satisfied },
  ];
  const doRevert = (group: "care" | "survey") =>
    revert.mutate({ squadId: mark.squadId, group }, { onError: (e) => toast.error(message(e, "Không trở về số máy được")) });

  return (
    <li className="space-y-3 rounded-md border border-border bg-surface p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <SquadMark squadId={mark.squadId} name={name} size={28} className="font-medium">
          {name}
          {teamName ? <span className="ml-1 text-xs font-normal text-muted">· {teamName}</span> : null}
        </SquadMark>
        <span className="text-xs text-muted">Máy đếm</span>
      </div>
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
        {cells.map((c) => (
          <div key={c.label} className="min-w-0 text-sm">
            <p className="text-muted">{c.label}</p>
            <p className="text-lg font-semibold tabular-nums">{c.value ?? "—"}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-1 text-sm md:grid-cols-2">
        <p data-testid="care-result"><span className="text-muted">Chăm đúng hẹn: </span>{carePass(mark)}</p>
        <p data-testid="survey-result"><span className="text-muted">Khách hài lòng: </span>{surveyPass(mark)}</p>
      </div>
      {mark.surveyPending ? (
        <p className="text-sm text-muted">
          Còn {mark.surveyPending} khách phải hỏi cho đủ tỷ lệ, hạn {formatDate(mark.surveyDeadline ?? "")}. Quá hạn chưa hỏi đủ thì tiêu chí này tính đạt.
        </p>
      ) : null}
      {mark.gnpmOrdersMissing ? <p className="text-sm text-muted">Số này chưa gồm {mark.gnpmOrdersMissing} đơn GNPM.</p> : null}
      {mark.careSource === "manual" ? (
        <SourceNote who={mark.setByName || mark.setBy} at={mark.setAt} reason={mark.careReason ?? ""} busy={revert.isPending} canEdit={canRecord}
          label={`Về số máy nhóm chăm khách — ${name}`} onRevert={() => doRevert("care")} />
      ) : null}
      {mark.surveySource === "manual" ? (
        <SourceNote who={mark.setByName || mark.setBy} at={mark.setAt} reason={mark.surveyReason ?? ""} busy={revert.isPending} canEdit={canRecord}
          label={`Về số máy nhóm hỏi khách — ${name}`} onRevert={() => doRevert("survey")} />
      ) : null}
      {canRecord ? (
        editing ? (
          <EditForm mark={mark} name={name} month={month} onDone={() => setEditing(false)} />
        ) : (
          <Button variant="outline" aria-label={`Sửa số — ${name}`} onClick={() => setEditing(true)}>Sửa số</Button>
        )
      ) : null}
      {canRecord ? (
        surveyError ? <QueryErrorNote error={surveyError} fallback="Không tải được khách cần hỏi" /> : <SurveyList items={survey} month={month} />
      ) : null}
      {canRecord ? (
        <section className="space-y-2">
          <Button variant="outline" aria-expanded={showAudit} onClick={() => setShowAudit(!showAudit)}>
            Mốc chăm ({milestones.length})
          </Button>
          {showAudit ? (
            milestonesError ? (
              <QueryErrorNote error={milestonesError} fallback="Không tải được mốc chăm khách" />
            ) : (
              <ul className="space-y-2">
                {milestones.map((m) => (
                  <MilestoneRow key={`${m.sourceSystem}:${m.orderId}:${m.kind}`} m={m} month={month} />
                ))}
              </ul>
            )
          ) : null}
        </section>
      ) : null}
    </li>
  );
}
